import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import { Alert, Box, Skeleton, Stack } from '@cmg/design-system';
import React from 'react';

import NotAccessible from '../../../common/components/layout/not-accessible/NotAccessible';
import { useFeatureToggles } from '../../../common/config';
import { SidePanelTitle } from '../components/side-bar/components/design-system';
import { useSideBar_OfferingDetails_Offering_BuySideQuery } from '../components/side-bar/graphql';
import { useOfferingSidePanelOfferingId } from '../hooks/useOfferingSidePanelMatch';
import OfferingNotes from './components/offering-notes/OfferingNotes';
import { useOfferingSidePanel_OfferingNotesQuery } from './graphql';

const OfferingNotesRoute: React.FC = () => {
  const offeringId = useOfferingSidePanelOfferingId();
  const canRead = useCheckPermissions([permissionsByEntity.Ioi.READ]);
  const { isOdsFunctionalityEnabled } = useFeatureToggles();

  const { loading: loadingOffering, data: offeringData } =
    useSideBar_OfferingDetails_Offering_BuySideQuery({
      skip: !canRead,
      variables: { offeringId },
    });

  const [publishedOffering] = offeringData?.publishedOfferings?.items ?? [];
  const isLinkedOffering = !!publishedOffering?.dataLabFull?.linkedOfferingId;
  const isExecutingOnPlatform = !!publishedOffering?.isExecutingOnPlatform;
  const isNotesNotAccessibleByFeatureFlag =
    isExecutingOnPlatform && isOdsFunctionalityEnabled && !isLinkedOffering;

  const {
    loading: loadingOfferingNotes,
    data,
    error,
  } = useOfferingSidePanel_OfferingNotesQuery({
    skip: !canRead || loadingOffering || isNotesNotAccessibleByFeatureFlag,
    variables: { offeringId },
  });

  if ((canRead && loadingOffering) || loadingOfferingNotes) {
    return (
      <Stack role="progressbar" gap={1}>
        <Skeleton sx={{ height: theme => theme.spacing(4) }} width="20%" />
        <Skeleton sx={{ height: theme => theme.spacing(4) }} variant="rectangular" width="100%" />
        <Skeleton sx={{ height: theme => theme.spacing(4) }} variant="rectangular" width="100%" />
        <Skeleton sx={{ height: theme => theme.spacing(2) }} variant="rectangular" width="40%" />
        <Skeleton sx={{ height: theme => theme.spacing(7) }} variant="rectangular" width="100%" />
      </Stack>
    );
  }

  if (!canRead || isNotesNotAccessibleByFeatureFlag) {
    return <NotAccessible subject="Notes" />;
  }

  return (
    <Box>
      <SidePanelTitle>Notes</SidePanelTitle>
      {error ? (
        <Alert severity="error">{error.message}</Alert>
      ) : (
        <OfferingNotes offeringId={offeringId} notes={[...(data?.offeringNotes.notes ?? [])]} />
      )}
    </Box>
  );
};

export default OfferingNotesRoute;
